import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../../services/api';

const HeroSection = () => {
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const data = await api.get('/heroes');
        const sorted = Array.isArray(data) ? data.sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
        setSlides(sorted);
      } catch (err) {
        console.error('Failed to fetch hero images:', err);
        setSlides([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHeroImages();
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="relative w-full h-[400px] md:h-[650px] lg:h-[800px] bg-gray-100 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gray-300 border-t-[#d60167] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  return (
    <section className="relative w-full overflow-hidden bg-[#d60167]">
      <div className="relative w-full h-[350px] md:h-[650px] lg:h-[800px]">
        {slides.map((slide, index) => (
          <div
            key={slide._id}
            className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
              index === activeSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <a href={slide.link || '#'} className="block w-full h-full">
              <img
                src={isMobile && slide.mobileImageUrl ? slide.mobileImageUrl : slide.imageUrl}
                alt={slide.title || 'Hero banner'}
                className="w-full h-full object-cover"
              />
            </a>
          </div>
        ))}

        {slides.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-black/20 hover:bg-black/40 text-white rounded-full transition-all duration-300"
            >
              <FiChevronLeft size={20} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-black/20 hover:bg-black/40 text-white rounded-full transition-all duration-300"
            >
              <FiChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 z-30 flex justify-center">
          <div className="flex gap-2 px-4 py-2 bg-black/10 backdrop-blur-sm rounded-full">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className="h-1.5 rounded-full transition-all duration-300 ease-out"
                style={{
                  width: '16px',
                  backgroundColor: index === activeSlide ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;